import React, { memo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Fab } from 'transfix-ui/components/Fab'
import { IconButton } from 'transfix-ui/components/IconButton'
import { OverflowMenu, useOverflowToggle } from 'transfix-ui/components/OverflowMenu'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { ILoad, ILoadDetailsState } from 'apps/fleet-planner/types'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors/load'
import { IStore } from 'apps/fleet-planner/utils/types/store'
import { IDropdownOption } from 'components/forms'
import { EllipsisIcon, DeleteIcon, ShareIcon } from 'transfix-icons'

interface ILoadDetailsActionsProps {
  load: ILoad
  isExternal?: boolean
}

export const LoadDetailsActions = memo<ILoadDetailsActionsProps>(({ isExternal, load }) => {
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFmsOneTimeSmsLoadShareEnabled } = getFlag('fmsOneTimeSmsLoadShare')
  const { isMobile } = useBreakpointContext()
  const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()
  const history = useHistory()
  const { load: shipment } = useSelector<IStore, ILoadDetailsState>(loadDetailsSelector)
  const { drivers } = shipment || {}
  const [driverOne] = drivers || []
  const isAssigned = driverOne?.id
  const shareSubUrl =
    isFmsOneTimeSmsLoadShareEnabled && !isExternal && isAssigned ? 'share-load-via-sms' : 'share'

  const deleteFileOption = {
    label: 'Delete Load',
    itemComponent: (
      <div className='flex flex-row items-center' data-test='dropdown-delete-load'>
        <DeleteIcon size='large' />
        <span className='ml2'>Delete Load</span>
      </div>
    ),
    action: () => load.id && history.push(`${history.location.pathname}/delete`),
  }

  const options: IDropdownOption[] = []

  if (!isExternal) {
    options.push(deleteFileOption)
  }

  /**
   * On desktop, this button is displayed separately on its own
   */
  if (isMobile) {
    options.push({
      label: 'Share Load',
      itemComponent: (
        <div className='flex flex-row items-center' data-test='dropdown-delete-load'>
          <ShareIcon size='large' />
          <span className='ml2'>Share Load</span>
        </div>
      ),
      action: () => load.id && history.push(`${history.location.pathname}/${shareSubUrl}`),
    })
  }

  return (
    <OverflowMenu
      options={options}
      trigger={
        <>
          {isMobile ? (
            <Fab
              aria-label='More...'
              color='primary'
              onClick={clickOverflowTrigger}
              data-test='dropdown'>
              <EllipsisIcon />
            </Fab>
          ) : (
            <IconButton
              data-test='dropdown'
              label='overflow-trigger'
              Icon={EllipsisIcon}
              onClick={clickOverflowTrigger}
              size='large'
            />
          )}
        </>
      }
      expanded={expanded}
      dropdownId='load-actions'
      onExpanded={setExpanded}
      alignment='right'
      className='mh2 mh3-m'
    />
  )
})
